import React, { useContext, useState } from 'react'
import styled from 'styled-components'
import { GoBackBar } from '../common/goBackBar'
import { bigPadding, getAssetLink, handleResponse } from '../common/utils'
import { AnalysisResult } from './analysisResult'
import SubmitOrCancel from 'live/components/forms/ui/submit-or-cancel'
import LiveTooltip from 'live/components/ui/tooltip'
import i18n from 'live/services/i18n'
import { getActiveRatings, getReturnPath, plansHaveEmptyFields } from './analysisUtils'
import { AssetTreeContext, ParamsContext } from '../qualityMonitoring/qualityMonitoring'
import { cloneDeep } from 'lodash'
import { editAnalysis } from '../common/apis'
import { ResultContext } from '../qualityAnalysis/qualityAnalysis'
import { SaveFooter } from '../common/components'
import { ProductInfo } from './productInfo'
import { ParticipantsInfo } from './participantsInfo'
import moment from 'moment-timezone'
import { motion } from 'framer-motion'
import { EmptyFields } from './analysisTypes'

const fieldSetVariants = {
    hidden: { opacity: 0, y: '100vh' },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 1 },

        transitionEnd: {
            opacity: 1,
            y: 0
        }
    }
}

export function AnalysisEditing({ selectedAnalysis }: { selectedAnalysis: AnalysisEvent }): JSX.Element {
    const urlParams = useContext(ParamsContext).split('/')
    // const [isEditing, setIsEditing] = useState<boolean>(false)
    const [isSaving, setIsSaving] = useState<boolean>(false)
    const [editedAnalysis, setEditedAnalysis] = useState<AnalysisEvent>(cloneDeep(selectedAnalysis))
    const assetTree = useContext(AssetTreeContext)
    const [emptyFields, setEmptyFields] = useState<EmptyFields>({ fields: [], trigger: false })

    function handleEditSubmit(): void {
        setIsSaving(true)
        editAnalysis(editedAnalysis).then(postResponse => {
            handleResponse(postResponse, 'analysis')
            setIsSaving(false)
            if (postResponse.status === 200) window.location.href = getAssetLink(getReturnPath(urlParams))
        })
    }

    const { packaging, appearance, texture, taste } = editedAnalysis
    const ratingInfo = { packaging, appearance, texture, taste }
    function updateActionPlans(newActionPlans: ActionPlan[]): void {
        setEditedAnalysis({ ...editedAnalysis, actionPlans: newActionPlans })
    }

    function disableSave(): boolean {
        if (editedAnalysis.actionPlans.length && plansHaveEmptyFields(editedAnalysis.actionPlans)) return true
        if (
            !editedAnalysis.batch ||
            !editedAnalysis.sku ||
            (!editedAnalysis.reference && editedAnalysis.reference !== false)
        ) {
            return true
        }
        const validParticipants = editedAnalysis.participants.filter(participant => participant)

        if (!validParticipants.length) return true

        return false
    }

    const saveDisabledStatus = disableSave()
    const isOver24HoursAgo = moment().diff(moment(editedAnalysis.createdAt), 'hours') > 24

    return (
        <>
            <GoBackBar />
            <StyledAnalysisEditin>
                <ResultContext.Provider
                    value={{
                        generalInfo: { ...editedAnalysis },
                        ratingInfo: ratingInfo as JSONConvertedRatingInfo,
                        actionPlans: editedAnalysis.actionPlans || [],
                        analysisType: editedAnalysis.analysisType,
                        finalScore: editedAnalysis.finalScore,
                        activeRatings: getActiveRatings(urlParams, assetTree),
                        setActionPlans: newActionPlans => updateActionPlans(newActionPlans)
                    }}
                >
                    <div className="generalInfo">
                        {isOver24HoursAgo ? (
                            <LiveTooltip
                                position="top"
                                content={i18n(
                                    'It is not possible to edit general information if the analysis is not from today'
                                )}
                                hide={false}
                            >
                                <div className="disableInputsOverlay" />
                            </LiveTooltip>
                        ) : null}
                        <motion.div variants={fieldSetVariants} initial={'hidden'} animate={'visible'} exit={'hidden'}>
                            <ProductInfo
                                generalInfo={{ ...editedAnalysis }}
                                displayColumn
                                setGeneralInfo={setEditedAnalysis}
                                emptyFields={emptyFields}
                                setEmptyFields={setEmptyFields}
                            />
                        </motion.div>
                        <motion.div variants={fieldSetVariants} initial={'hidden'} animate={'visible'} exit={'hidden'}>
                            <ParticipantsInfo
                                generalInfo={{ ...editedAnalysis }}
                                setGeneralInfo={setEditedAnalysis}
                                emptyFields={emptyFields}
                                setEmptyFields={setEmptyFields}
                            />
                        </motion.div>
                    </div>
                    <AnalysisResult renderConditional={true} />
                </ResultContext.Provider>
                <SaveFooter>
                    <LiveTooltip
                        position="top"
                        content={i18n('Please make sure all fields are filled correctly')}
                        hide={!saveDisabledStatus}
                    >
                        <div>
                            <SubmitOrCancel
                                onCancel={() => (window.location.href = getAssetLink(getReturnPath(urlParams)))}
                                onSubmit={handleEditSubmit}
                                saving={isSaving}
                                disabled={saveDisabledStatus}
                                buttonMessage={`${i18n('Save')}`}
                            />
                        </div>
                    </LiveTooltip>
                </SaveFooter>
            </StyledAnalysisEditin>
        </>
    )
}

const StyledAnalysisEditin = styled.div`
    padding: 40px ${bigPadding}px;
    position: relative;

    .generalInfo {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-gap: 20px;
        margin-bottom: 20px;
        align-items: start;
        position: relative;
        .disableInputsOverlay {
            position: absolute;
            width: 100%;
            height: 100%;
            z-index: 3;
            background: var(--gray-0);
            opacity: 0.5;
        }
    }
`
